/**
 * Notifications Screen - MOBILE ONLY
 * 
 * Notification preferences.
 * Stacked flow - back button, no bottom tabs.
 */

import React, { useCallback } from 'react';
import { View, Text, ScrollView, StyleSheet, Switch, Pressable, ActivityIndicator } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useRouter } from 'expo-router';
import { useNotificationPreferences, NotificationPreferences } from '../../../hooks/useNotificationPreferences';
import { color, text, space, touch, icon, layout, border } from '../../../shared/tokens/mobile';
import { ScreenContainer, SectionHeader, ListCard } from '../../../components/v2/mobile';

export default function NotificationsScreen() {
  const router = useRouter();
  const { preferences, isLoading, updatePreference } = useNotificationPreferences();

  const handleBack = useCallback(() => {
    router.back();
  }, [router]);

  if (isLoading) {
    return (
      <ScreenContainer>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={color.primary} />
        </View>
      </ScreenContainer>
    );
  }

  return (
    <ScreenContainer>
      <ScrollView
        style={styles.scroll}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
      >
        {/* HEADER */}
        <View style={styles.header}>
          <Pressable onPress={handleBack} style={styles.backButton} accessibilityRole="button" accessibilityLabel="Go back">
            <Ionicons name="chevron-back" size={icon.lg} color={color.textPrimary} />
          </Pressable>
          <Text style={styles.title}>Notifications</Text>
          <View style={styles.spacer} />
        </View>

        {/* GENERAL */}
        <View style={styles.section}>
          <SectionHeader label="General" />
          <ListCard>
            <ToggleRow
              title="Push Notifications"
              subtitle="Receive alerts on your device"
              value={preferences.pushEnabled}
              onValueChange={(v) => updatePreference('pushEnabled', v)}
            />
            <ToggleRow
              title="Email Notifications"
              subtitle="Receive updates via email"
              value={preferences.emailEnabled}
              onValueChange={(v) => updatePreference('emailEnabled', v)}
              hideBorder
            />
          </ListCard>
        </View>

        {/* ACTIVITY */}
        <View style={styles.section}>
          <SectionHeader label="Activity" />
          <ListCard>
            <ToggleRow
              title="Mentions"
              subtitle="When someone mentions you"
              value={preferences.mentionsEnabled}
              onValueChange={(v) => updatePreference('mentionsEnabled', v)}
            />
            <ToggleRow
              title="Opportunity Updates"
              subtitle="Changes to your opportunities"
              value={preferences.updatesEnabled}
              onValueChange={(v) => updatePreference('updatesEnabled', v)}
            />
            <ToggleRow
              title="Due Diligence Reminders"
              subtitle="Upcoming and overdue items"
              value={preferences.ddRemindersEnabled}
              onValueChange={(v) => updatePreference('ddRemindersEnabled', v)}
            />
            <ToggleRow
              title="Task Reminders"
              subtitle="Tasks assigned to you"
              value={preferences.taskRemindersEnabled}
              onValueChange={(v) => updatePreference('taskRemindersEnabled', v)}
            />
            <ToggleRow
              title="Ideas & Voting"
              subtitle="New ideas and votes on opportunities"
              value={preferences.ideaNotificationsEnabled}
              onValueChange={(v) => updatePreference('ideaNotificationsEnabled', v)}
              hideBorder
            />
          </ListCard>
        </View>

        {/* INFO */}
        <View style={styles.infoContainer}>
          <Text style={styles.infoText}>
            You can change notification permissions in your device settings.
          </Text>
        </View>
      </ScrollView>
    </ScreenContainer>
  );
}

// =============================================================================
// TOGGLE ROW COMPONENT
// =============================================================================

interface ToggleRowProps {
  title: string;
  subtitle: string;
  value: boolean;
  onValueChange: (value: boolean) => void;
  hideBorder?: boolean;
}

function ToggleRow({ title, subtitle, value, onValueChange, hideBorder }: ToggleRowProps) {
  return (
    <View style={[styles.toggleRow, !hideBorder && styles.toggleRowBorder]}>
      <View style={styles.toggleContent}>
        <Text style={styles.toggleTitle}>{title}</Text>
        <Text style={styles.toggleSubtitle}>{subtitle}</Text>
      </View>
      <Switch
        value={value}
        onValueChange={onValueChange}
        trackColor={{ false: color.disabled, true: color.primaryLight }}
        thumbColor={value ? color.primary : color.surface}
        accessibilityLabel={`${title} toggle`}
      />
    </View>
  );
}

// =============================================================================
// STYLES
// =============================================================================

const styles = StyleSheet.create({
  scroll: {
    flex: 1,
  },
  scrollContent: {
    paddingBottom: layout.scrollBottomPadding,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },

  // Header
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: space.sm,
    paddingVertical: space.sm,
  },
  backButton: {
    width: touch.minimum,
    height: touch.minimum,
    alignItems: 'center',
    justifyContent: 'center',
  },
  title: {
    flex: 1,
    fontSize: text.body.fontSize,
    fontWeight: text.bodyMedium.fontWeight,
    color: color.textPrimary,
    textAlign: 'center',
  },
  spacer: {
    width: touch.minimum,
  },

  // Sections
  section: {
    paddingHorizontal: space.md,
    marginBottom: space.lg,
  },

  // Toggle row
  toggleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: space.md,
    paddingHorizontal: space.md,
    gap: space.md,
  },
  toggleRowBorder: {
    borderBottomWidth: border.width.thin,
    borderBottomColor: color.divider,
  },
  toggleContent: {
    flex: 1,
  },
  toggleTitle: {
    fontSize: text.body.fontSize,
    fontWeight: text.bodyMedium.fontWeight,
    color: color.textPrimary,
  },
  toggleSubtitle: {
    fontSize: text.caption.fontSize,
    fontWeight: text.caption.fontWeight,
    color: color.textSecondary,
    marginTop: space.xs / 2,
  },

  // Info
  infoContainer: {
    paddingHorizontal: space.lg,
    paddingVertical: space.md,
  },
  infoText: {
    fontSize: text.caption.fontSize,
    fontWeight: text.caption.fontWeight,
    color: color.textMuted,
    textAlign: 'center',
  },
});
